import { useState, useRef, useEffect } from 'react';
import './ImageEditor.css';

const ImageEditorNew = ({ image, initialDimensions, onUpdate, onClose }) => {
  const [activeTab, setActiveTab] = useState('crop'); // 'crop', 'filters', 'adjust', 'text'
  const [isSaving, setIsSaving] = useState(false);

  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);

  // Crop state
  const [cropX, setCropX] = useState(0);
  const [cropY, setCropY] = useState(0);
  const [cropWidth, setCropWidth] = useState(100);
  const [cropHeight, setCropHeight] = useState(100);
  const [aspectRatio, setAspectRatio] = useState(null); // null = free, or number like 1, 16/9, etc

  // Filter states
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);
  const [blur, setBlur] = useState(0);
  const [grayscale, setGrayscale] = useState(0);
  const [sepia, setSepia] = useState(0);
  const [hueRotate, setHueRotate] = useState(0);

  // Text overlay state
  const [textOverlays, setTextOverlays] = useState([]);
  const [currentText, setCurrentText] = useState('');
  const [textColor, setTextColor] = useState('#ffffff');
  const [textSize, setTextSize] = useState(24);

  const [isDragging, setIsDragging] = useState(false);
  const [dragHandle, setDragHandle] = useState(null); // null, 'move', 'tl', 'tr', 'bl', 'br', 't', 'b', 'l', 'r'
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartY, setDragStartY] = useState(0);

  // Text drag state
  const [draggedTextId, setDraggedTextId] = useState(null);
  const [selectedTextId, setSelectedTextId] = useState(null);

  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [naturalWidth, setNaturalWidth] = useState(0);
  const [naturalHeight, setNaturalHeight] = useState(0);
  const redrawTimeoutRef = useRef(null);
  const imageObjRef = useRef(null);

  const imgSrc = typeof image === 'string' ? image : image?.src;

  // Load image and set initial crop area
  useEffect(() => {
    if (!imgSrc) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const w = initialDimensions?.width || img.width;
      const h = initialDimensions?.height || img.height;

      setNaturalWidth(img.width);
      setNaturalHeight(img.height);

      // Set initial crop to full image
      setCropWidth(w);
      setCropHeight(h);
      setCropX(0);
      setCropY(0);

      setImageLoaded(true);
    };

    img.onerror = () => {
      const img2 = new Image();
      img2.onload = () => {
        const w = initialDimensions?.width || img2.width;
        const h = initialDimensions?.height || img2.height;
        setNaturalWidth(img2.width);
        setNaturalHeight(img2.height);
        setCropWidth(w);
        setCropHeight(h);
        setImageLoaded(true);
      };
      img2.src = imgSrc;
    };

    img.src = imgSrc;
  }, [imgSrc, initialDimensions]);

  // Draw image with crop overlay and filters (optimized with debouncing)
  useEffect(() => {
    if (!imageLoaded || !canvasRef.current) return;

    // Clear any pending redraw
    if (redrawTimeoutRef.current) {
      cancelAnimationFrame(redrawTimeoutRef.current);
    }

    // Use requestAnimationFrame for smooth updates
    redrawTimeoutRef.current = requestAnimationFrame(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d', { willReadFrequently: false });

      // Use cached image if available
      const drawImage = () => {
        // Canvas size
        const maxWidth = 800;
        const maxHeight = 600;
        const imgAspect = naturalWidth / naturalHeight;

        let displayWidth, displayHeight;
        if (imgAspect > maxWidth / maxHeight) {
          displayWidth = maxWidth;
          displayHeight = maxWidth / imgAspect;
        } else {
          displayHeight = maxHeight;
          displayWidth = maxHeight * imgAspect;
        }

        canvas.width = displayWidth;
        canvas.height = displayHeight;

        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Apply CSS filters
        ctx.filter = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%) blur(${blur}px) grayscale(${grayscale}%) sepia(${sepia}%) hue-rotate(${hueRotate}deg)`;

        // Save context state
        ctx.save();

        // Apply transformations
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
        ctx.scale(scale, scale);

        // Draw image
        if (imageObjRef.current) {
          ctx.drawImage(
            imageObjRef.current,
            -displayWidth / 2,
            -displayHeight / 2,
            displayWidth,
            displayHeight
          );
        }

        ctx.restore();

        // Reset filter for overlay drawing
        ctx.filter = 'none';

        // Draw crop overlay
        const scaleFactorX = displayWidth / naturalWidth;
        const scaleFactorY = displayHeight / naturalHeight;

        const overlayX = cropX * scaleFactorX;
        const overlayY = cropY * scaleFactorY;
        const overlayW = cropWidth * scaleFactorX;
        const overlayH = cropHeight * scaleFactorY;

        // Dark overlay outside crop area
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.fillRect(0, 0, canvas.width, overlayY); // top
        ctx.fillRect(0, overlayY, overlayX, overlayH); // left
        ctx.fillRect(overlayX + overlayW, overlayY, canvas.width - (overlayX + overlayW), overlayH); // right
        ctx.fillRect(0, overlayY + overlayH, canvas.width, canvas.height - (overlayY + overlayH)); // bottom

        // Crop border
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.strokeRect(overlayX, overlayY, overlayW, overlayH);

        // Draw handles
        const handleSize = 12;
        const handles = [
          { x: overlayX, y: overlayY }, // tl
          { x: overlayX + overlayW, y: overlayY }, // tr
          { x: overlayX, y: overlayY + overlayH }, // bl
          { x: overlayX + overlayW, y: overlayY + overlayH }, // br
          { x: overlayX + overlayW / 2, y: overlayY }, // t
          { x: overlayX + overlayW / 2, y: overlayY + overlayH }, // b
          { x: overlayX, y: overlayY + overlayH / 2 }, // l
          { x: overlayX + overlayW, y: overlayY + overlayH / 2 }, // r
        ];

        handles.forEach(handle => {
          ctx.fillStyle = '#fff';
          ctx.fillRect(handle.x - handleSize / 2, handle.y - handleSize / 2, handleSize, handleSize);
          ctx.strokeStyle = '#007bff';
          ctx.lineWidth = 2;
          ctx.strokeRect(handle.x - handleSize / 2, handle.y - handleSize / 2, handleSize, handleSize);
        });

        // Center grid lines
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.lineWidth = 1;
        ctx.setLineDash([5, 5]);
        // Vertical thirds
        ctx.beginPath();
        ctx.moveTo(overlayX + overlayW / 3, overlayY);
        ctx.lineTo(overlayX + overlayW / 3, overlayY + overlayH);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(overlayX + (2 * overlayW) / 3, overlayY);
        ctx.lineTo(overlayX + (2 * overlayW) / 3, overlayY + overlayH);
        ctx.stroke();
        // Horizontal thirds
        ctx.beginPath();
        ctx.moveTo(overlayX, overlayY + overlayH / 3);
        ctx.lineTo(overlayX + overlayW, overlayY + overlayH / 3);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(overlayX, overlayY + (2 * overlayH) / 3);
        ctx.lineTo(overlayX + overlayW, overlayY + (2 * overlayH) / 3);
        ctx.stroke();
        ctx.setLineDash([]);

        // Draw text overlays
        textOverlays.forEach(textOverlay => {
          ctx.save();
          ctx.font = `${textOverlay.size * scaleFactorX}px Arial`;
          ctx.fillStyle = textOverlay.color;
          ctx.textBaseline = 'top';

          const textX = textOverlay.x * scaleFactorX;
          const textY = textOverlay.y * scaleFactorY;

          // Draw text
          ctx.fillText(textOverlay.text, textX, textY);

          // Draw selection box if selected
          if (selectedTextId === textOverlay.id) {
            const textMetrics = ctx.measureText(textOverlay.text);
            const textWidth = textMetrics.width;
            const textHeight = textOverlay.size * scaleFactorX;

            ctx.strokeStyle = '#667eea';
            ctx.lineWidth = 2;
            ctx.setLineDash([5, 3]);
            ctx.strokeRect(textX - 5, textY - 5, textWidth + 10, textHeight + 10);
            ctx.setLineDash([]);

            // Draw corner handles
            const handleSize = 8;
            ctx.fillStyle = '#667eea';
            // Top-left
            ctx.fillRect(textX - 5 - handleSize / 2, textY - 5 - handleSize / 2, handleSize, handleSize);
            // Top-right
            ctx.fillRect(textX + textWidth + 5 - handleSize / 2, textY - 5 - handleSize / 2, handleSize, handleSize);
            // Bottom-left
            ctx.fillRect(textX - 5 - handleSize / 2, textY + textHeight + 5 - handleSize / 2, handleSize, handleSize);
            // Bottom-right
            ctx.fillRect(textX + textWidth + 5 - handleSize / 2, textY + textHeight + 5 - handleSize / 2, handleSize, handleSize);
          }

          ctx.restore();
        });
      };

      // If image is already cached, draw immediately
      if (imageObjRef.current) {
        drawImage();
      } else {
        // Load and cache image
        const img = new Image();
        img.crossOrigin = 'anonymous';

        img.onload = () => {
          imageObjRef.current = img;
          drawImage();
        };

        img.onerror = () => {
          const img2 = new Image();
          img2.onload = () => {
            imageObjRef.current = img2;
            drawImage();
          };
          img2.src = imgSrc;
        };

        img.src = imgSrc;
      }
    });

    return () => {
      if (redrawTimeoutRef.current) {
        cancelAnimationFrame(redrawTimeoutRef.current);
      }
    };
  }, [imageLoaded, imgSrc, rotation, scale, flipH, flipV, cropX, cropY, cropWidth, cropHeight, naturalWidth, naturalHeight, brightness, contrast, saturation, blur, grayscale, sepia, hueRotate, textOverlays, selectedTextId]);

  const getMousePosition = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const handleMouseDown = (e) => {
    const { x, y } = getMousePosition(e);
    const canvas = canvasRef.current;

    const scaleFactorX = canvas.width / naturalWidth;
    const scaleFactorY = canvas.height / naturalHeight;

    // Check if clicking on a text overlay (reverse order for top-most text)
    const ctx = canvas.getContext('2d');
    for (let i = textOverlays.length - 1; i >= 0; i--) {
      const textOverlay = textOverlays[i];
      ctx.font = `${textOverlay.size * scaleFactorX}px Arial`;
      const textMetrics = ctx.measureText(textOverlay.text);
      const textWidth = textMetrics.width;
      const textHeight = textOverlay.size * scaleFactorX;

      const textX = textOverlay.x * scaleFactorX;
      const textY = textOverlay.y * scaleFactorY;

      // Check if click is within text bounds
      if (x >= textX - 5 && x <= textX + textWidth + 5 && y >= textY - 5 && y <= textY + textHeight + 10) {
        setSelectedTextId(textOverlay.id);
        setDraggedTextId(textOverlay.id);
        setIsDragging(true);
        setDragHandle('text');
        setDragStartX(x);
        setDragStartY(y);
        return;
      }
    }

    // If clicked outside text, deselect
    setSelectedTextId(null);

    const overlayX = cropX * scaleFactorX;
    const overlayY = cropY * scaleFactorY;
    const overlayW = cropWidth * scaleFactorX;
    const overlayH = cropHeight * scaleFactorY;

    const handleSize = 12;
    const handles = [
      { name: 'tl', x: overlayX, y: overlayY },
      { name: 'tr', x: overlayX + overlayW, y: overlayY },
      { name: 'bl', x: overlayX, y: overlayY + overlayH },
      { name: 'br', x: overlayX + overlayW, y: overlayY + overlayH },
      { name: 't', x: overlayX + overlayW / 2, y: overlayY },
      { name: 'b', x: overlayX + overlayW / 2, y: overlayY + overlayH },
      { name: 'l', x: overlayX, y: overlayY + overlayH / 2 },
      { name: 'r', x: overlayX + overlayW, y: overlayY + overlayH / 2 },
    ];

    // Check if clicking on a handle
    for (const handle of handles) {
      if (Math.abs(x - handle.x) < handleSize && Math.abs(y - handle.y) < handleSize) {
        setIsDragging(true);
        setDragHandle(handle.name);
        setDragStartX(x);
        setDragStartY(y);
        return;
      }
    }

    // Check if clicking inside crop area to move
    if (x >= overlayX && x <= overlayX + overlayW && y >= overlayY && y <= overlayY + overlayH) {
      setIsDragging(true);
      setDragHandle('move');
      setDragStartX(x);
      setDragStartY(y);
    } else if (activeTab === 'crop') {
      // Clicked outside crop area - auto apply crop if crop has been modified
      const isCropModified = cropX !== 0 || cropY !== 0 ||
                            cropWidth !== naturalWidth ||
                            cropHeight !== naturalHeight;

      if (isCropModified) {
        // Small delay to ensure it doesn't interfere with drag operations
        setTimeout(() => {
          if (!isDragging) {
            handleApplyCrop();
          }
        }, 100);
      }
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !dragHandle) {
      // Update cursor based on position
      updateCursor(e);
      return;
    }

    const { x, y } = getMousePosition(e);
    const canvas = canvasRef.current;

    const scaleFactorX = canvas.width / naturalWidth;
    const scaleFactorY = canvas.height / naturalHeight;

    const dx = (x - dragStartX) / scaleFactorX;
    const dy = (y - dragStartY) / scaleFactorY;

    // Handle text dragging
    if (dragHandle === 'text' && draggedTextId !== null) {
      setTextOverlays(prevOverlays =>
        prevOverlays.map(overlay =>
          overlay.id === draggedTextId
            ? {
                ...overlay,
                x: Math.max(0, Math.min(naturalWidth - 50, overlay.x + dx)),
                y: Math.max(0, Math.min(naturalHeight - 20, overlay.y + dy))
              }
            : overlay
        )
      );
      setDragStartX(x);
      setDragStartY(y);
      return;
    }

    if (dragHandle === 'move') {
      // Move crop area
      const newX = Math.max(0, Math.min(naturalWidth - cropWidth, cropX + dx));
      const newY = Math.max(0, Math.min(naturalHeight - cropHeight, cropY + dy));
      setCropX(newX);
      setCropY(newY);
      setDragStartX(x);
      setDragStartY(y);
    } else {
      // Resize crop area
      let newX = cropX;
      let newY = cropY;
      let newW = cropWidth;
      let newH = cropHeight;

      const minSize = 50;

      if (dragHandle.includes('l')) {
        newW = Math.max(minSize, cropWidth - dx);
        newX = cropX + (cropWidth - newW);
      }
      if (dragHandle.includes('r')) {
        newW = Math.max(minSize, cropWidth + dx);
      }
      if (dragHandle.includes('t')) {
        newH = Math.max(minSize, cropHeight - dy);
        newY = cropY + (cropHeight - newH);
      }
      if (dragHandle.includes('b')) {
        newH = Math.max(minSize, cropHeight + dy);
      }

      // Maintain aspect ratio if set
      if (aspectRatio) {
        if (dragHandle === 'br' || dragHandle === 'tr' || dragHandle === 'bl' || dragHandle === 'tl') {
          // Corner handles - maintain aspect
          const currentAspect = newW / newH;
          if (currentAspect > aspectRatio) {
            newW = newH * aspectRatio;
            if (dragHandle.includes('l')) {
              newX = cropX + cropWidth - newW;
            }
          } else {
            newH = newW / aspectRatio;
            if (dragHandle.includes('t')) {
              newY = cropY + cropHeight - newH;
            }
          }
        }
      }

      // Keep within bounds
      if (newX < 0) { newW += newX; newX = 0; }
      if (newY < 0) { newH += newY; newY = 0; }
      if (newX + newW > naturalWidth) newW = naturalWidth - newX;
      if (newY + newH > naturalHeight) newH = naturalHeight - newY;

      setCropX(newX);
      setCropY(newY);
      setCropWidth(newW);
      setCropHeight(newH);
      setDragStartX(x);
      setDragStartY(y);
    }
  };

  const updateCursor = (e) => {
    if (isDragging) return;

    const { x, y } = getMousePosition(e);
    const canvas = canvasRef.current;

    const scaleFactorX = canvas.width / naturalWidth;
    const scaleFactorY = canvas.height / naturalHeight;

    const overlayX = cropX * scaleFactorX;
    const overlayY = cropY * scaleFactorY;
    const overlayW = cropWidth * scaleFactorX;
    const overlayH = cropHeight * scaleFactorY;

    const handleSize = 12;
    const handles = [
      { name: 'tl', x: overlayX, y: overlayY, cursor: 'nwse-resize' },
      { name: 'tr', x: overlayX + overlayW, y: overlayY, cursor: 'nesw-resize' },
      { name: 'bl', x: overlayX, y: overlayY + overlayH, cursor: 'nesw-resize' },
      { name: 'br', x: overlayX + overlayW, y: overlayY + overlayH, cursor: 'nwse-resize' },
      { name: 't', x: overlayX + overlayW / 2, y: overlayY, cursor: 'ns-resize' },
      { name: 'b', x: overlayX + overlayW / 2, y: overlayY + overlayH, cursor: 'ns-resize' },
      { name: 'l', x: overlayX, y: overlayY + overlayH / 2, cursor: 'ew-resize' },
      { name: 'r', x: overlayX + overlayW, y: overlayY + overlayH / 2, cursor: 'ew-resize' },
    ];

    // Check handles
    for (const handle of handles) {
      if (Math.abs(x - handle.x) < handleSize && Math.abs(y - handle.y) < handleSize) {
        canvas.style.cursor = handle.cursor;
        return;
      }
    }

    // Check if inside crop area
    if (x >= overlayX && x <= overlayX + overlayW && y >= overlayY && y <= overlayY + overlayH) {
      canvas.style.cursor = 'move';
    } else {
      canvas.style.cursor = 'default';
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragHandle(null);
    setDraggedTextId(null);
  };

  const handleAspectRatioChange = (ratio) => {
    setAspectRatio(ratio);
    if (ratio) {
      // Adjust crop area to match aspect ratio
      const currentAspect = cropWidth / cropHeight;
      if (currentAspect > ratio) {
        // Too wide, reduce width
        const newWidth = cropHeight * ratio;
        setCropX(cropX + (cropWidth - newWidth) / 2);
        setCropWidth(newWidth);
      } else {
        // Too tall, reduce height
        const newHeight = cropWidth / ratio;
        setCropY(cropY + (cropHeight - newHeight) / 2);
        setCropHeight(newHeight);
      }
    }
  };

  const handleApplyCrop = () => {
    if (!imgSrc) return;

    const img = imageObjRef.current || new Image();

    const applyCrop = () => {
      try {
        // Create canvas with cropped dimensions
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = Math.floor(cropWidth);
        tempCanvas.height = Math.floor(cropHeight);
        const tempCtx = tempCanvas.getContext('2d');

        // Apply current filters
        tempCtx.filter = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%) blur(${blur}px) grayscale(${grayscale}%) sepia(${sepia}%) hue-rotate(${hueRotate}deg)`;

        // Apply transformations
        tempCtx.save();
        tempCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2);
        tempCtx.rotate((rotation * Math.PI) / 180);
        tempCtx.scale(flipH ? -1 : 1, flipV ? -1 : 1);

        // Draw cropped portion
        tempCtx.drawImage(
          img,
          cropX, cropY, cropWidth, cropHeight,
          -cropWidth / 2, -cropHeight / 2, cropWidth, cropHeight
        );
        tempCtx.restore();

        // Get the cropped image as data URL
        const croppedDataURL = tempCanvas.toDataURL('image/png');

        // Load the cropped image and update everything
        const croppedImg = new Image();
        croppedImg.onload = () => {
          const newWidth = cropWidth;
          const newHeight = cropHeight;

          imageObjRef.current = croppedImg;
          setNaturalWidth(newWidth);
          setNaturalHeight(newHeight);
          setCropX(0);
          setCropY(0);
          setCropWidth(newWidth);
          setCropHeight(newHeight);
          setRotation(0);
          setFlipH(false);
          setFlipV(false);

          // Update text overlay positions relative to new crop
          setTextOverlays(prevOverlays =>
            prevOverlays.map(overlay => ({
              ...overlay,
              x: overlay.x - cropX,
              y: overlay.y - cropY
            })).filter(overlay =>
              overlay.x >= 0 && overlay.x <= newWidth &&
              overlay.y >= 0 && overlay.y <= newHeight
            )
          );
        };
        croppedImg.src = croppedDataURL;
      } catch (error) {
        console.error('Error applying crop:', error);
        alert('Failed to apply crop. Please try again.');
      }
    };

    if (imageObjRef.current) {
      applyCrop();
    } else {
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        imageObjRef.current = img;
        applyCrop();
      };
      img.src = imgSrc;
    }
  };

  const handleSave = () => {
    if (!imgSrc || isSaving) return;

    setIsSaving(true);

    // Use cached image for faster save
    const img = imageObjRef.current || new Image();

    const processSave = () => {
      try {
        // Create canvas for final image
        let finalWidth = cropWidth;
        let finalHeight = cropHeight;

        if (rotation % 180 !== 0) {
          [finalWidth, finalHeight] = [finalHeight, finalWidth];
        }

        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = Math.floor(finalWidth * scale);
        tempCanvas.height = Math.floor(finalHeight * scale);
        const tempCtx = tempCanvas.getContext('2d');

        // Apply CSS filters to final image
        tempCtx.filter = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%) blur(${blur}px) grayscale(${grayscale}%) sepia(${sepia}%) hue-rotate(${hueRotate}deg)`;

        // Apply transformations
        tempCtx.save();
        tempCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2);
        tempCtx.rotate((rotation * Math.PI) / 180);
        tempCtx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
        tempCtx.scale(scale, scale);

        // Draw cropped portion of image
        tempCtx.drawImage(
          img,
          cropX, cropY, cropWidth, cropHeight, // source crop area
          -cropWidth / 2, -cropHeight / 2, cropWidth, cropHeight // destination
        );
        tempCtx.restore();

        // Reset filter for text overlay
        tempCtx.filter = 'none';

        // Draw text overlays (adjusted for crop area)
        textOverlays.forEach(overlay => {
          // Adjust text position relative to crop area
          const textX = (overlay.x - cropX) * scale;
          const textY = (overlay.y - cropY) * scale;

          // Only draw if text is within crop bounds
          if (overlay.x >= cropX && overlay.x <= cropX + cropWidth &&
              overlay.y >= cropY && overlay.y <= cropY + cropHeight) {
            tempCtx.save();
            tempCtx.font = `${overlay.size * scale}px Arial`;
            tempCtx.fillStyle = overlay.color;
            tempCtx.textBaseline = 'top';
            tempCtx.fillText(overlay.text, textX, textY);
            tempCtx.restore();
          }
        });

        const dataURL = tempCanvas.toDataURL('image/png');

        // Call onUpdate and onClose only once
        onUpdate({
          src: dataURL,
          rotation: 0,
          scale: 1,
          flipH: false,
          flipV: false,
          bgColor: 'transparent',
        });

        onClose();
      } catch (error) {
        console.error('Error saving image:', error);
        setIsSaving(false);
        alert('Failed to save image. Please try again.');
      }
    };

    if (imageObjRef.current) {
      // Use cached image immediately
      processSave();
    } else {
      // Load image first
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        imageObjRef.current = img;
        processSave();
      };

      img.onerror = () => {
        setIsSaving(false);
        const img2 = new Image();
        img2.crossOrigin = 'anonymous';
        img2.onload = () => {
          imageObjRef.current = img2;
          processSave();
        };
        img2.onerror = () => {
          setIsSaving(false);
          alert('Failed to load image. Please try again.');
        };
        img2.src = imgSrc;
      };

      img.src = imgSrc;
    }
  };

  const handleReset = () => {
    setRotation(0);
    setScale(1);
    setFlipH(false);
    setFlipV(false);
    setAspectRatio(null);
    setCropX(0);
    setCropY(0);
    setCropWidth(naturalWidth);
    setCropHeight(naturalHeight);
    // Reset filters
    setBrightness(100);
    setContrast(100);
    setSaturation(100);
    setBlur(0);
    setGrayscale(0);
    setSepia(0);
    setHueRotate(0);
    // Reset text overlays
    setTextOverlays([]);
    setCurrentText('');
  };

  const handleAddText = () => {
    if (currentText.trim()) {
      const newOverlay = {
        text: currentText,
        x: 50,
        y: 50,
        color: textColor,
        size: textSize,
        id: Date.now(),
      };
      setTextOverlays([...textOverlays, newOverlay]);
      setCurrentText('');
    }
  };

  const handleRemoveText = (id) => {
    setTextOverlays(textOverlays.filter(overlay => overlay.id !== id));
  };

  const applyFilterPreset = (preset) => {
    switch (preset) {
      case 'vintage':
        setBrightness(110);
        setContrast(90);
        setSaturation(80);
        setSepia(30);
        break;
      case 'bw':
        setGrayscale(100);
        setContrast(110);
        break;
      case 'vivid':
        setSaturation(150);
        setContrast(110);
        setBrightness(105);
        break;
      case 'cool':
        setHueRotate(180);
        setSaturation(120);
        break;
      case 'warm':
        setSepia(20);
        setBrightness(105);
        setSaturation(110);
        break;
      default:
        break;
    }
  };

  // Keyboard shortcuts for crop area adjustment
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!imageLoaded) return;

      const step = e.shiftKey ? 10 : 1;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          setCropX(x => Math.max(0, x - step));
          break;
        case 'ArrowRight':
          e.preventDefault();
          setCropX(x => Math.min(naturalWidth - cropWidth, x + step));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setCropY(y => Math.max(0, y - step));
          break;
        case 'ArrowDown':
          e.preventDefault();
          setCropY(y => Math.min(naturalHeight - cropHeight, y + step));
          break;
        case 'Escape':
          e.preventDefault();
          if (!isSaving) {
            onClose();
          }
          break;
        case 'Enter':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            if (!isSaving) {
              handleSave();
            }
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [imageLoaded, naturalWidth, naturalHeight, cropWidth, cropHeight, onClose, isSaving]);

  if (!image) return null;

  return (
    <div className="image-editor-overlay">
      <div className="image-editor-modal-new">
        <div className="image-editor-header">
          <h3>✨ Professional Image Editor</h3>
          <button className="image-editor-close" onClick={onClose}>✕</button>
        </div>

        <div className="image-editor-content-new">
          {/* Canvas Area */}
          <div className="image-editor-canvas-wrapper" ref={containerRef}>
            <canvas
              ref={canvasRef}
              className="image-editor-canvas-new"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              style={{ cursor: dragHandle ? 'grabbing' : 'grab' }}
            />
            <div className="canvas-hint">
              💡 Drag corners to resize • Drag inside to move • Arrow keys for precision • Esc to close • Ctrl+Enter to save
            </div>
          </div>

          {/* Right Sidebar Controls */}
          <div className="image-editor-sidebar">
            {/* Tab Navigation */}
            <div className="editor-tabs">
              <button
                className={`editor-tab ${activeTab === 'crop' ? 'active' : ''}`}
                onClick={() => setActiveTab('crop')}
              >
                ✂️ Crop
              </button>
              <button
                className={`editor-tab ${activeTab === 'filters' ? 'active' : ''}`}
                onClick={() => setActiveTab('filters')}
              >
                🎨 Filters
              </button>
              <button
                className={`editor-tab ${activeTab === 'adjust' ? 'active' : ''}`}
                onClick={() => setActiveTab('adjust')}
              >
                🔧 Adjust
              </button>
              <button
                className={`editor-tab ${activeTab === 'text' ? 'active' : ''}`}
                onClick={() => setActiveTab('text')}
              >
                📝 Text
              </button>
            </div>

            {/* Tab Content */}
            <div className="tab-content">
              {/* CROP TAB */}
              {activeTab === 'crop' && (
                <>
                  {/* Aspect Ratio Presets */}
                  <div className="control-section">
                    <label className="control-label">📐 Aspect Ratio</label>
                    <div className="aspect-ratio-buttons">
                      <button
                        className={aspectRatio === null ? 'active' : ''}
                        onClick={() => handleAspectRatioChange(null)}
                      >
                        Free
                      </button>
                      <button
                        className={aspectRatio === 1 ? 'active' : ''}
                        onClick={() => handleAspectRatioChange(1)}
                      >
                        1:1
                      </button>
                      <button
                        className={aspectRatio === 16/9 ? 'active' : ''}
                        onClick={() => handleAspectRatioChange(16/9)}
                      >
                        16:9
                      </button>
                      <button
                        className={aspectRatio === 4/3 ? 'active' : ''}
                        onClick={() => handleAspectRatioChange(4/3)}
                      >
                        4:3
                      </button>
                      <button
                        className={aspectRatio === 3/2 ? 'active' : ''}
                        onClick={() => handleAspectRatioChange(3/2)}
                      >
                        3:2
                      </button>
                    </div>
                  </div>

                  {/* Rotation */}
                  <div className="control-section">
                    <label className="control-label">↻ Rotate</label>
                    <div className="button-group">
                      <button onClick={() => setRotation((rotation - 90 + 360) % 360)}>
                        ↶ -90°
                      </button>
                      <button onClick={() => setRotation((rotation + 90) % 360)}>
                        ↷ +90°
                      </button>
                      <button onClick={() => setRotation(0)}>
                        Reset
                      </button>
                    </div>
                    <div className="rotation-value">{rotation}°</div>
                  </div>

                  {/* Flip */}
                  <div className="control-section">
                    <label className="control-label">🔄 Flip</label>
                    <div className="button-group">
                      <button
                        className={flipH ? 'active' : ''}
                        onClick={() => setFlipH(!flipH)}
                      >
                        ↔ Horizontal
                      </button>
                      <button
                        className={flipV ? 'active' : ''}
                        onClick={() => setFlipV(!flipV)}
                      >
                        ↕ Vertical
                      </button>
                    </div>
                  </div>

                  {/* Scale */}
                  <div className="control-section">
                    <label className="control-label">🔍 Scale: {scale.toFixed(1)}x</label>
                    <input
                      type="range"
                      min="0.5"
                      max="2"
                      step="0.1"
                      value={scale}
                      onChange={(e) => setScale(parseFloat(e.target.value))}
                      className="range-slider"
                    />
                    <div className="range-labels">
                      <span>50%</span>
                      <span>200%</span>
                    </div>
                  </div>

                  {/* Crop Info */}
                  <div className="control-section">
                    <label className="control-label">📏 Crop Size</label>
                    <div className="crop-info">
                      {Math.round(cropWidth)} × {Math.round(cropHeight)} px
                    </div>
                  </div>

                  {/* Apply Crop Button */}
                  <div className="control-section">
                    <button onClick={handleApplyCrop} className="add-text-btn">
                      ✂️ Apply Crop & Continue Editing
                    </button>
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '8px', textAlign: 'center' }}>
                      Crop the image and continue editing the cropped version
                    </div>
                  </div>
                </>
              )}

              {/* FILTERS TAB */}
              {activeTab === 'filters' && (
                <>
                  {/* Filter Presets */}
                  <div className="control-section">
                    <label className="control-label">✨ Quick Presets</label>
                    <div className="filter-presets">
                      <button onClick={() => applyFilterPreset('vintage')} className="preset-btn">
                        📷 Vintage
                      </button>
                      <button onClick={() => applyFilterPreset('bw')} className="preset-btn">
                        ⚫ B&W
                      </button>
                      <button onClick={() => applyFilterPreset('vivid')} className="preset-btn">
                        🌈 Vivid
                      </button>
                      <button onClick={() => applyFilterPreset('cool')} className="preset-btn">
                        ❄️ Cool
                      </button>
                      <button onClick={() => applyFilterPreset('warm')} className="preset-btn">
                        🔥 Warm
                      </button>
                    </div>
                  </div>

                  {/* Brightness */}
                  <div className="control-section">
                    <label className="control-label">☀️ Brightness: {brightness}%</label>
                    <input
                      type="range"
                      min="0"
                      max="200"
                      value={brightness}
                      onChange={(e) => setBrightness(Number(e.target.value))}
                      className="range-slider"
                    />
                    <div className="range-labels">
                      <span>Dark</span>
                      <span>Bright</span>
                    </div>
                  </div>

                  {/* Contrast */}
                  <div className="control-section">
                    <label className="control-label">🌗 Contrast: {contrast}%</label>
                    <input
                      type="range"
                      min="0"
                      max="200"
                      value={contrast}
                      onChange={(e) => setContrast(Number(e.target.value))}
                      className="range-slider"
                    />
                    <div className="range-labels">
                      <span>Low</span>
                      <span>High</span>
                    </div>
                  </div>

                  {/* Saturation */}
                  <div className="control-section">
                    <label className="control-label">🎨 Saturation: {saturation}%</label>
                    <input
                      type="range"
                      min="0"
                      max="200"
                      value={saturation}
                      onChange={(e) => setSaturation(Number(e.target.value))}
                      className="range-slider"
                    />
                    <div className="range-labels">
                      <span>Gray</span>
                      <span>Vibrant</span>
                    </div>
                  </div>
                </>
              )}

              {/* ADJUST TAB */}
              {activeTab === 'adjust' && (
                <>
                  {/* Blur */}
                  <div className="control-section">
                    <label className="control-label">💨 Blur: {blur}px</label>
                    <input
                      type="range"
                      min="0"
                      max="20"
                      value={blur}
                      onChange={(e) => setBlur(Number(e.target.value))}
                      className="range-slider"
                    />
                    <div className="range-labels">
                      <span>Sharp</span>
                      <span>Blurry</span>
                    </div>
                  </div>

                  {/* Grayscale */}
                  <div className="control-section">
                    <label className="control-label">⚫ Grayscale: {grayscale}%</label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={grayscale}
                      onChange={(e) => setGrayscale(Number(e.target.value))}
                      className="range-slider"
                    />
                    <div className="range-labels">
                      <span>Color</span>
                      <span>B&W</span>
                    </div>
                  </div>

                  {/* Sepia */}
                  <div className="control-section">
                    <label className="control-label">📜 Sepia: {sepia}%</label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={sepia}
                      onChange={(e) => setSepia(Number(e.target.value))}
                      className="range-slider"
                    />
                    <div className="range-labels">
                      <span>Normal</span>
                      <span>Vintage</span>
                    </div>
                  </div>

                  {/* Hue Rotate */}
                  <div className="control-section">
                    <label className="control-label">🌈 Hue: {hueRotate}°</label>
                    <input
                      type="range"
                      min="0"
                      max="360"
                      value={hueRotate}
                      onChange={(e) => setHueRotate(Number(e.target.value))}
                      className="range-slider"
                    />
                    <div className="range-labels">
                      <span>0°</span>
                      <span>360°</span>
                    </div>
                  </div>
                </>
              )}

              {/* TEXT TAB */}
              {activeTab === 'text' && (
                <>
                  {/* Add Text */}
                  <div className="control-section">
                    <label className="control-label">📝 Add Text Overlay</label>
                    <input
                      type="text"
                      placeholder="Enter text..."
                      value={currentText}
                      onChange={(e) => setCurrentText(e.target.value)}
                      className="text-input"
                    />
                  </div>

                  {/* Text Color */}
                  <div className="control-section">
                    <label className="control-label">🎨 Text Color</label>
                    <input
                      type="color"
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                      className="color-input"
                    />
                  </div>

                  {/* Text Size */}
                  <div className="control-section">
                    <label className="control-label">📏 Text Size: {textSize}px</label>
                    <input
                      type="range"
                      min="12"
                      max="72"
                      value={textSize}
                      onChange={(e) => setTextSize(Number(e.target.value))}
                      className="range-slider"
                    />
                    <div className="range-labels">
                      <span>Small</span>
                      <span>Large</span>
                    </div>
                  </div>

                  {/* Add Text Button */}
                  <div className="control-section">
                    <button onClick={handleAddText} className="add-text-btn">
                      ➕ Add Text
                    </button>
                  </div>

                  {/* Text Overlays List */}
                  {textOverlays.length > 0 && (
                    <div className="control-section">
                      <label className="control-label">📋 Text Overlays ({textOverlays.length})</label>
                      <div className="text-overlays-list">
                        {textOverlays.map(overlay => (
                          <div key={overlay.id} className="text-overlay-item">
                            <span style={{ color: overlay.color }}>
                              {overlay.text.substring(0, 20)}...
                            </span>
                            <button onClick={() => handleRemoveText(overlay.id)} className="remove-text-btn">
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="image-editor-footer-new">
          <button className="btn-reset" onClick={handleReset} disabled={isSaving}>
            🔄 Reset All
          </button>
          <div className="footer-actions">
            <button className="btn-cancel" onClick={onClose} disabled={isSaving}>
              Cancel
            </button>
            <button className="btn-save" onClick={handleSave} disabled={isSaving}>
              {isSaving ? '⏳ Saving...' : '✓ Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageEditorNew;
